/**
 * <s-club-popup> — Opens the Members' Club sign-up dialog on product pages.
 *
 * Shows the child <dialog> once after a short delay, then stays quiet:
 *   - dismissed: hidden for `data-snooze-days` (default 14) days
 *   - signed up: hidden for good
 * After a successful sign-up the page reloads with `customer_posted=true`;
 * the dialog reopens once so the shopper sees the success message.
 *
 * Attributes:
 *   data-delay        — milliseconds before opening (default 4000)
 *   data-snooze-days  — days to wait after a dismissal (default 14)
 *   data-design-mode  — present in the theme editor: open on load, never store state
 *   data-mobile-only  — only open on phone-width screens (used on the homepage)
 */
const STORAGE_KEY = 'gw-club-popup';
const MOBILE_MQ = window.matchMedia('(max-width: 47.999rem)');

class SClubPopup extends HTMLElement {
  #dialog;
  #ac;
  #timer;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const designMode = this.hasAttribute('data-design-mode');

    if (designMode) {
      // In the editor, follow the preview width live so switching to mobile shows it straight away.
      if (this.hasAttribute('data-mobile-only')) {
        MOBILE_MQ.addEventListener('change', (event) => {
          if (event.matches) this.#open();
          else if (this.#dialog.open) this.#dialog.close();
        }, opts);
      }
      this.#open();
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('customer_posted') === 'true') {
      this.#store({ state: 'subscribed' });
      this.#open();
      return;
    }

    // A submit that comes back without customer_posted=true failed validation:
    // reopen once so the shopper sees the error next to the field.
    const pending = this.#read()?.state === 'pending';

    this.#dialog.addEventListener('submit', () => this.#store({ state: 'pending' }), opts);
    this.#dialog.addEventListener('close', () => {
      if (this.#read()?.state !== 'pending') this.#store({ state: 'dismissed', at: Date.now() });
    }, opts);

    if (pending) {
      this.#store({ state: 'dismissed', at: Date.now() });
      this.#open();
      return;
    }

    if (!this.#shouldShow()) return;
    const delay = Number(this.dataset.delay) || 4000;
    this.#timer = window.setTimeout(() => this.#open(), delay);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    window.clearTimeout(this.#timer);
  }

  #shouldShow() {
    const saved = this.#read();
    if (!saved) return true;
    if (saved.state === 'subscribed') return false;
    const snoozeDays = Number(this.dataset.snoozeDays) || 14;
    return Date.now() - (saved.at || 0) > snoozeDays * 86400000;
  }

  #open() {
    if (this.#dialog.open || document.querySelector('dialog[open]')) return;
    // data-mobile-only (homepage): phones only. Checked at open time, after the page has its real width.
    if (this.hasAttribute('data-mobile-only') && !MOBILE_MQ.matches) return;
    // On phones, the beanie/tee pairing offer pops up instead; don't stack two pop-ups.
    if (document.querySelector('s-addon-offer[data-mobile-popup]') && MOBILE_MQ.matches) return;
    this.#dialog.showModal();
  }

  #read() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    } catch {
      return null;
    }
  }

  #store(value) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    } catch {
      /* storage unavailable: the popup simply shows again next visit */
    }
  }
}

if (!customElements.get('s-club-popup')) customElements.define('s-club-popup', SClubPopup);
